import { type FC, useCallback, useMemo } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import type { ApiStockLot } from "../../../../../../api/lots";
import type { StockGatheringReserveItem } from "../../../../../../api/stockGatherings";
import { resolveStockGatheringCompletionReserveScan } from "../../helpers/resolveStockGatheringCompletionReserveScan";

const RESERVE_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-gathering-completion.card.scan-lot",
    title: "Сканируйте этикетку партии ТМЦ",
    description: "Найдём резерв по партии и откроем форму комплектования.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockGatheringCompletionScanPlaceholder}. */
interface StockGatheringCompletionScanPlaceholderProps {
    /** Резервы текущей КВ. */
    reserves: StockGatheringReserveItem[];
    /** Включён ли сценарий сканирования партии. */
    enabled: boolean;
    /**
     * Открывает форму комплектования резерва.
     * @param reserveCode код найденного резерва
     */
    onOpenReserve: (reserveCode: number) => void;
    /**
     * Открывает окно выбора при нескольких резервах на отсканированную партию.
     * @param payload партия и совпавшие резервы
     */
    onAmbiguousReserves: (payload: { lot: ApiStockLot; reserves: StockGatheringReserveItem[] }) => void;
}

/** Регистрирует сценарий сканирования партии в карточке комплектования КВ. */
export const StockGatheringCompletionScanPlaceholder: FC<StockGatheringCompletionScanPlaceholderProps> = ({
    reserves,
    enabled,
    onOpenReserve,
    onAmbiguousReserves,
}) => {
    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStockGatheringCompletionReserveScan(barcode, reserves);

            if (result.type === "notFound") {
                notification.warning(`Партия для скана "${barcode}" не найдена.`);
                return;
            }

            if (result.type === "reserveNotFound") {
                notification.warning("Просканированная партия не относится к резервам выбранной КВ.");
                return;
            }

            if (result.type === "ambiguous") {
                onAmbiguousReserves({ lot: result.lot, reserves: result.reserves });
                return;
            }

            onOpenReserve(result.reserve.Code);
        },
        [onAmbiguousReserves, onOpenReserve, reserves],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled,
            priority: "screen",
            captureInEditable: false,
            hint: RESERVE_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.gatheringCompletion.card.scanReserve",
        }),
        [enabled, handleScan],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};
